/**
 * MultiSelectCombobox Component
 * Searchable multi-select dropdown for categories, specialties, and expertise
 * Maximum 300 lines (constitutional requirement)
 */

import * as React from 'react';
import { Check, ChevronsUpDown, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export type MultiSelectOption = {
  id: string;
  name: string;
};

type MultiSelectComboboxProps = {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  error?: string;
  required?: boolean;
  /** Cap badges rendered in the trigger; overflow collapses into "+N more". */
  maxVisibleBadges?: number;
};

/**
 * MultiSelectCombobox - Searchable multi-select dropdown
 * 
 * Features:
 * - Search/filter options
 * - Multi-selection with checkboxes
 * - Display selected items as removable badges
 * - Keyboard navigation
 * - Loading state
 * - Error state
 * 
 * Usage with react-hook-form:
 * ```tsx
 * <Controller
 *   name="categoryIds"
 *   control={control}
 *   render={({ field }) => (
 *     <MultiSelectCombobox
 *       options={categories}
 *       value={field.value || []}
 *       onChange={field.onChange}
 *       placeholder="Select categories"
 *     />
 *   )}
 * />
 * ```
 */
export function MultiSelectCombobox({
  options,
  value = [],
  onChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  emptyText = 'No items found.',
  disabled = false,
  className,
  loading = false,
  error,
  required = false,
  maxVisibleBadges,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Get selected option names for display
  const selectedOptions = React.useMemo(() => {
    return options.filter((opt) => value.includes(opt.id));
  }, [options, value]);

  const visibleOptions =
    maxVisibleBadges == null
      ? selectedOptions
      : selectedOptions.slice(0, Math.max(0, maxVisibleBadges));
  const hiddenOptions = selectedOptions.slice(visibleOptions.length);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    return options.filter((opt) =>
      opt.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  // Toggle selection
  const handleToggle = (optionId: string) => {
    const newValue = value.includes(optionId)
      ? value.filter((id) => id !== optionId)
      : [...value, optionId];
    onChange(newValue);
  };

  // Remove specific item
  const handleRemove = (optionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(value.filter((id) => id !== optionId));
  };

  // Clear all selections
  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        disabled={disabled || loading}
        onClick={() => setOpen(!open)}
        className={cn(
          'flex w-full items-center justify-between min-h-[40px] h-auto rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-red-500 focus-visible:ring-red-500',
          selectedOptions.length > 0 && 'py-2'
        )}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selectedOptions.length === 0 ? (
            <span className="text-gray-500">
              {placeholder}
              {required && <span className="text-red-500 ml-1">*</span>}
            </span>
          ) : (
            <>
              {visibleOptions.map((option) => (
                <span
                  key={option.id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-sage-soft text-sage rounded"
                >
                  {option.name}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(option.id, e)}
                    className="hover:bg-white/60 rounded-full p-0.5"
                    aria-label={`Remove ${option.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {hiddenOptions.length > 0 && (
                <span
                  className="inline-flex items-center px-2 py-1 text-sm text-text-secondary"
                  title={hiddenOptions.map((opt) => opt.name).join(', ')}
                >
                  +{hiddenOptions.length} more · {selectedOptions.length} selected
                </span>
              )}
            </>
          )}
        </div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {/* Dropdown Content */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-[300px] overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-500"
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-[200px] p-1">
            {loading ? (
              <div className="py-6 text-center text-sm">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm">{emptyText}</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleToggle(option.id)}
                    className={cn(
                      'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                      'hover:bg-gray-100 focus:bg-gray-100',
                      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                    )}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'bg-bquest-green border-bquest-green text-white'
                          : 'border-gray-400'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span>{option.name}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Clear All Button */}
          {selectedOptions.length > 0 && (
            <div className="border-t p-2">
              <button
                type="button"
                onClick={handleClearAll}
                className="w-full text-sm px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded"
              >
                Clear all ({selectedOptions.length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}

